import { useEffect } from 'react'

/**
 * Asks the browser to show its native "leave site?" confirmation when the tab or window is closed,
 * reloaded, or navigated away from while `enabled` is true.
 *
 * Two deliberate choices, both of which are what QMD-4225 turned on:
 *
 * 1. `addEventListener` — never `window.onbeforeunload`. `utils/tabCounter.ts` assigns to that
 *    property on module load and `pages/home/AppLogout` reassigns it on the inactivity logout, so a
 *    handler installed there is silently replaced. Listener-channel handlers are immune to both.
 * 2. `preventDefault()` **and** `returnValue = ''`. Chromium honours the former; Safari and older
 *    engines only honour the latter, so setting both is what makes the dialog cross-browser.
 *
 * Callers decide when to arm it. Both visit surfaces arm it on `useCallInProgress` — see that hook
 * for which lifecycle states count, and why an open-but-call-less visit deliberately never prompts.
 *
 * Known limitation, not fixable from here: browsers only render the dialog once the document has
 * *sticky user activation* — i.e. the provider has clicked/typed in the page at least once since it
 * loaded. A tab that was restored or opened programmatically and never interacted with still closes
 * without prompting.
 */
export function useBeforeUnloadWarning(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return undefined

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [enabled])
}
